// Delivering it: the sessions that run, the issues and pull requests they move,
// the fleet boxes they run ON, the templates and prompt vars they run WITH, the
// artifacts they leave behind, and CI.
// Split out of `../capability-manifest.ts`, which is now the barrel over this
// directory. A GROUP, never a meaning: a capability belongs to whichever group
// its `REST_ROUTES` neighbours are in, so these are absent from the barrel and a
// caller reads `CAPABILITY_MANIFEST`. `./manifest` is the only place they compose,
// and `capability-manifest.test.ts` proves the five are a PARTITION of it.

import { BOX_TOKEN_LEG, NO_ARTIFACT_UPLOAD_UI, NO_RELEASE_LEDGER_REPAIR_UI } from './reasons';
import { type Capability, gap, mcp, na, yes } from './surface';

export const DELIVERY_CAPABILITIES: Readonly<Record<string, Capability>> = {
  // ── sessions ──────────────────────────────────────────────────────────────
  'session.list': {
    rest: yes,
    // `session.list` is PER REPO (the paged collection). This id is the
    // account-wide top-N tile, whose REST shape is a bounded head served as a
    // page envelope with `next_cursor` always null — mirroring that onto MCP
    // would copy the truncation, so the honest read comes first.
    mcp: gap('the `session` verb lists BY REPO; the account-wide head has no action'),
    ui: yes,
  },
  'session.list_by_project': { rest: yes, mcp: mcp('session.list'), ui: yes },
  'session.get': { rest: yes, mcp: mcp('session.get'), ui: yes },
  'session.events': {
    rest: yes,
    mcp: na(
      'an SSE tail is not a JSON-RPC shape; `session.events` polls the same rows (the `task.run_tail` precedent)',
    ),
    ui: yes,
  },
  // The `.jsonl` download and the MCP poll are the same read in two envelopes —
  // one store, one `toEventRow` projection, paged on MCP because a JSON-RPC
  // result is one message rather than a stream a caller can stop reading.
  'session.events_export': { rest: yes, mcp: mcp('session.events'), ui: yes },
  'session.replay': { rest: yes, mcp: mcp('session.replay'), ui: yes },
  // `POST /v1/fleet/runs/:runId/message` — deliberately NOT `/v1/sessions/:id/message`,
  // which the capability id's `session.` prefix would suggest. The mailbox is
  // keyed on a fleet RUN id (`run_<32 hex>`, minted off-cluster) while
  // `/v1/sessions/:id` addresses an `agent_sessions` row (`ses_<32 hex>`): two
  // id spaces, two things. A `/v1` path is a one-way door, so it names the id it
  // actually takes. Consumer-or-absent on the fleet NATS connection, exactly as
  // the verb is: no connection, no subject, neither door.
  'session.message': {
    rest: yes,
    mcp: mcp('message'),
    ui: gap('live session views are read-only'),
  },
  'stream.subscribe': {
    rest: yes,
    mcp: na('a multiplexed SSE feed is not a JSON-RPC shape'),
    ui: yes,
  },

  // ── issues ────────────────────────────────────────────────────────────────
  'issue.list': { rest: yes, mcp: mcp('issue.list'), ui: yes },
  'issue.get': { rest: yes, mcp: mcp('issue.get'), ui: yes },
  'issue.handoff': { rest: yes, mcp: mcp('issue.handoff'), ui: yes },
  'issue.escalate': { rest: yes, mcp: mcp('issue.escalate'), ui: yes },

  // #7785: close one escalation by id. REST only for now: no surface lists open tellings yet, so
  // an MCP verb and a dashboard control would both be asked for ids nothing shows them.
  'escalation.ack': {
    rest: yes,
    mcp: gap('no MCP verb yet: no surface lists open escalations to act on'),
    ui: gap('no dashboard surface lists open escalations yet'),
  },

  // ── pull requests + review ────────────────────────────────────────────────
  'pr.list': { rest: yes, mcp: mcp('pr.list'), ui: yes },
  'pr.get': { rest: yes, mcp: mcp('pr.get'), ui: yes },
  'pr.approve_pending': { rest: yes, mcp: mcp('pr.approve_pending'), ui: yes },
  'pr.skip_pending': { rest: yes, mcp: mcp('pr.skip_pending'), ui: yes },
  'pr.undo_decision': {
    rest: yes,
    mcp: mcp('pr.undo_decision'),
    ui: na('a rare operator correction, reached over REST and MCP; no dashboard control asked for'),
  },
  'pr.pending_approvals': { rest: yes, mcp: mcp('pr.approvals'), ui: yes },
  // `GET /v1/approved-prs` — the account-wide head of the OPEN pull requests a
  // maintainer has APPROVED. The queue above answers a different question (a PR
  // still waiting for a human decision); this one answers "what has already been
  // approved, and is it still mergeable".
  'pr.approved_open': {
    rest: yes,
    mcp: gap(
      'no MCP verb lists the account-wide approved open pull requests; `pr list` is per repo and `pr approvals` lists the asks still parked on a human decision',
    ),
    ui: yes,
  },
  // `POST /v1/prs/:id/rearm-babysit` — the maintainer's re-arm of a spent babysit CI
  // self-fix budget, over the one use case the `pr rearm_babysit` action also calls.
  'pr.rearm_babysit': {
    rest: yes,
    mcp: mcp('pr.rearm_babysit'),
    ui: gap('the PR view shows an escalated babysit but cannot re-arm its spent budget'),
  },
  // The Home metric strip's two headline reads (`GET /v1/overview/counts`,
  // `GET /v1/overview/burn`). Kept beside the approvals queue: one of the four
  // counts IS that queue's size.
  'overview.counts': {
    rest: yes,
    mcp: gap(
      'no verb returns the account-wide headline counts; `pr list` and `project list` are per collection',
    ),
    ui: yes,
  },
  'overview.burn': {
    rest: yes,
    mcp: gap("no verb sums the account's recorded session cost over a window"),
    ui: yes,
  },
  'review.status': { rest: yes, mcp: mcp('review.status'), ui: yes },
  // `GET /v1/repos/:owner/:repo/reviews` (arc 101 slice 06) — the REPO-wide read
  // beside the per-PR one above. A different question with different facts: the
  // per-PR runs carry the task row only, while this folds the event-side truths a
  // maintainer grades the reviewer on (which model ran, the findings it KEPT, and
  // whether it ran blind) off `task_run_events`, once, in `listRepoReviewRuns`.
  'review.list': {
    rest: yes,
    mcp: gap(
      'no verb yet — the `review` resource addresses ONE pull request on both its actions, and a repo-wide list needs its own address; add one when an agent needs to reason across a repo’s reviews rather than the PR in front of it',
    ),
    // `ui: yes` carries no address — the type says so — so the evidence goes here:
    // the tab is declared UNCONDITIONALLY in `lib/repoTabs.ts` (every repo, every
    // role; only Secrets is role-gated), mounted from `routes/RepoDetail.tsx`, and
    // `components/RepoReviewsTab.tsx` renders each run through
    // `components/ReviewBrainBadge.tsx`, which gives `model` / `blind` / `unknown`
    // three distinct pills and reports `run_provenance` beside the verdict rather
    // than merged into it — the partition this capability exists to keep.
    ui: yes,
  },
  // `POST /v1/prs/:id/review-runs` — the write sibling of the GET above, over
  // the SAME `ReviewControl`. REST addresses a PR by row id and the capability
  // by `(repo full name, PR number)` (a review task is matched on its envelope,
  // not on a `pull_requests` join), so the route translates the address and
  // changes nothing else.
  'review.rerun': {
    rest: yes,
    mcp: mcp('review.rerun'),
    ui: gap('the PR view lists review runs but cannot re-run one'),
  },

  // ── fleet boxes ───────────────────────────────────────────────────────────
  'box.list': { rest: yes, mcp: mcp('box.list'), ui: yes },
  'box.add': { rest: yes, mcp: mcp('box.add'), ui: yes },
  'box.add_ssh': { rest: yes, mcp: mcp('box.enroll_ssh'), ui: yes },
  // The console's revoke is the details panel's token retry after a decommission whose
  // cascade failed (#6563): re-runs the idempotent token kill on a revoked machine.
  'box.revoke': { rest: yes, mcp: mcp('box.revoke'), ui: yes },
  // Plan 101 slice 02 (#3002): drain → revoke → audit `runner.decommissioned`, one
  // composition on both doors. The console's "Remove machine" (the details panel, #6563).
  'box.decommission': { rest: yes, mcp: mcp('box.decommission'), ui: yes },
  // Both doors carry the per-machine `verdict` from ONE pure function (#6557,
  // `machineVerdict` in `../entities/runner/machine-verdict.ts`): `boxes[].verdict` on
  // the REST body, `boxes[].verdict` on each non-revoked `fleet_status` row. Its
  // `jobs_state` (#6746) folds the SAME held-jobs read on both doors (`fleetBoxJobs`;
  // MCP through the `heldJobs` port, this account's own boxes only).
  'box.utilization': { rest: yes, mcp: mcp('box.fleet_status'), ui: yes },
  // Fleet Live (#6777): the org's live pipeline, one builder behind REST and the `fleet_live`
  // stream topic. The Fleet Live canvas (#6778, #6779) is its UI and is not built yet. MCP is
  // `na`: an animated live view, not a question an agent asks — every number on it is a
  // stored row the agent already reads by verb (`task`, `pr`, `box.fleet_status`, `status`).
  'box.fleet_live': {
    rest: yes,
    mcp: na(
      'a live canvas feed, not an agent question: its numbers are rows the task, pr, box and status verbs already read',
    ),
    ui: gap(
      'the Fleet Live section (#6778 Command Deck and Topology, #6779 Core Intelligence) is not built yet',
    ),
  },
  // The box LIFECYCLE plane. It was MCP-only, which meant an operator's own
  // tooling could not do over HTTP what their agent could do over JSON-RPC; the
  // eight rows below are one wire face over the SAME `BoxControl` the `box`
  // verb holds (`apps/api/src/routes/runners.ts` `createFleetLifecycleRoutes`),
  // and the UI half is `/fleet` — the machine details panel (`<MachinePanel/>`, #6563)
  // for the ones addressed at one box, `<FleetVersionsPanel/>` +
  // `<FleetRolloutModal/>` for the three fleet-wide ones.
  //
  // `box.get` is `yes` and not the `webhook.get` `na`: the roster's
  // `RunnerRosterResource` projection carries neither `owner_kind` nor `system_prompt` for
  // a PAT-listed row, so the dialog CANNOT render from the list the way the
  // templates screen does — it point-reads, and two of its writes would
  // otherwise author against fields nothing had shown.
  'box.get': { rest: yes, mcp: mcp('box.get'), ui: yes },
  'box.versions': { rest: yes, mcp: mcp('box.versions'), ui: yes },
  // The rollout dialog's build picker: the installable rows of the picked
  // channel, deduped across targets. The dialog reads it and nothing else does.
  'box.releases': { rest: yes, mcp: mcp('box.releases'), ui: yes },
  'box.drain': { rest: yes, mcp: mcp('box.drain'), ui: yes },
  // A drain HOLDS the box out (`runners.drained_at`) until this clears it. The UI
  // half is `<BoxDrainPanel/>`: a drained box (`drained_iso` on every runner body)
  // reads Drained on its roster card and offers Undrain in place of a second drain.
  'box.undrain': { rest: yes, mcp: mcp('box.undrain'), ui: yes },
  // Every job a box holds for the account, paused / resumed in one press (#6751); unlike the
  // drain the box keeps taking work. One control (`apps/api/src/deps/runner-pause-jobs.ts`).
  'box.pause_jobs': {
    rest: yes,
    mcp: mcp('box.pause_jobs'),
    ui: gap('the Fleet card has no pause control yet (#6753)'),
  },
  'box.resume_jobs': {
    rest: yes,
    mcp: mcp('box.resume_jobs'),
    ui: gap('the Fleet card has no pause control yet (#6753)'),
  },
  // The box's OWN process log — boot, enrolment, self-update, NATS, the busy
  // marker — which no other capability can reach: `task.run_tail` carries the
  // MODEL's story and is silent for a box with no run, which is exactly the box
  // worth reading. MCP-only for now, and both gaps are real rather than
  // deliberate: a REST leg is a thin wrapper nobody has needed yet, and the
  // console has no log viewer, so an operator reads this through the harness.
  'box.logs': {
    rest: gap('no REST leg yet — the MCP verb is the only door'),
    mcp: mcp('box.logs'),
    ui: gap('the console has no runner-log viewer'),
  },
  'box.set_owner': { rest: yes, mcp: mcp('box.set_owner'), ui: yes },
  // The console can REPLACE or CLEAR a briefing but never edit one in place, and
  // that is a property of the capability rather than a shortfall of the screen:
  // every door (`maskBox`, the verb, the write's own echo) serves a 120-char
  // preview, so no caller anywhere can read back the prose it stored.
  // The operator's attestation that a box runs every coding job in its own
  // container — the ONE thing that widens a box's claim past a single tenant
  // (`runners.isolation_attested_at`, the only input to `isBoxIsolated` in
  // `apps/worker/src/dispatch/assigner.ts`).
  //
  // ONE ROW, TWO ADDRESSES, on the `task.reassign` precedent: `attest_isolation`
  // and `withdraw_isolation` are a capability and its inverse over one
  // `BoxControl.attestIsolation`, distinguished only by the boolean the verb
  // derives from the action name. Two rows would duplicate the REST/UI verdicts
  // of one capability and let them drift — and the scope check below would then
  // have nothing to compare, which is exactly how a laxer gate on the WITHDRAWAL
  // would ship unnoticed: a `write:runners` holder able to de-attest any box on
  // the platform silently stops another tenant's fleet from claiming.
  //
  // Keyed `box.isolation` rather than either verb name, because ids are the REST
  // catalog's ids (see the header) and the REST leg this row owes toggles BOTH
  // directions — an id naming only the grant would misdescribe its own future
  // route. Both surfaces are `gap`, not `na`: the lifecycle plane's every other
  // action has a REST twin (`createFleetLifecycleRoutes`) and a `/fleet` control,
  // and nothing about this one refuses either. It is MCP-only today because the
  // verb landed first.
  'box.isolation': {
    rest: gap('no REST leg yet — the MCP verb is the only door'),
    mcp: mcp('box.attest_isolation', 'box.withdraw_isolation'),
    ui: gap('the console has no isolation-attestation control'),
  },
  'box.set_prompt': { rest: yes, mcp: mcp('box.set_prompt'), ui: yes },
  // #2268: REST first because the infra fleet needed it (the names were prod `UPDATE`s).
  // The control is `apps/api` `deps/box-rename.ts`, ready for a `box rename` action.
  'box.rename': {
    rest: yes,
    mcp: gap('the `box` verb has no rename action yet'),
    ui: yes,
  },
  // #6556: one label for many boxes, all or none, both doors over `apps/api`
  // `deps/box-server-label.ts`. The Fleet page's "Name this server" (#6566) is the
  // control, in the group header and the machine-panel head.
  'box.server_label': {
    rest: yes,
    mcp: mcp('box.set_server_label'),
    ui: yes,
  },
  // #6767: send a GitHub issue or pull request to ONE machine with a hard target, both doors
  // over `apps/api` `deps/box-send.ts`. The Fleet page's machine card is the control (#6770):
  // its Send work form, opened from Now's "Send", the simplified card's "Send work", the card
  // menu's "Send work" and an idle card's "Assign work".
  'box.send': {
    rest: yes,
    mcp: mcp('box.send'),
    ui: yes,
  },
  // #6768: move one job or a batch to another machine, and preview the move on every other
  // machine, both doors over ONE control (`apps/api` `deps/box-move.ts`). The Fleet page's
  // controls land with #6771 (drag and the Move to menu) and #6772 (the Move work panel).
  // #7790: lend a box to another account, both doors over ONE control (`apps/api`
  // `deps/box-grants.ts`). The dashboard's accept screen is the follow-up.
  'box.grant_offer': {
    rest: yes,
    mcp: mcp('box.grant_offer'),
    ui: gap('no dashboard surface yet (#7790 follow-up)'),
  },
  'box.grant_accept': {
    rest: yes,
    mcp: mcp('box.grant_accept'),
    ui: gap('no dashboard surface yet (#7790 follow-up)'),
  },
  'box.grant_revoke': {
    rest: yes,
    mcp: mcp('box.grant_revoke'),
    ui: gap('no dashboard surface yet (#7790 follow-up)'),
  },
  'box.grant_list': {
    rest: yes,
    mcp: mcp('box.grant_list'),
    ui: gap('no dashboard surface yet (#7790 follow-up)'),
  },
  'box.move': {
    rest: yes,
    mcp: mcp('box.move'),
    ui: gap('the Fleet card has no move control yet (#6771, #6772)'),
  },
  'box.move_preview': {
    rest: yes,
    mcp: mcp('box.move_preview'),
    ui: gap('the Fleet card has no move control yet (#6771, #6772)'),
  },
  // #6740: the box's jobs-at-once cap (`runners.max_slots`, 1..8, default 4 —
  // the bounds in `../entities/runner/max-slots.ts`), both doors over `apps/api`
  // `deps/box-max-slots.ts`. ONE control, TWO REST routes: `set_max_slots` sets
  // one box (the console's "Jobs at once" row and card-menu picker), and
  // `reset_max_slots` is the owner's undo for multi-job, every live box of the
  // account back to 1 (a literal path with no `:id`). This map is keyed by REST
  // id: one row per id, one MCP action each, so every address is claimed exactly
  // once and a lookup by either route id tells the truth about both surfaces.
  'box.max_slots': {
    rest: yes,
    mcp: mcp('box.set_max_slots'),
    ui: yes,
  },
  'box.max_slots_reset': {
    rest: yes,
    mcp: mcp('box.reset_max_slots'),
    ui: gap(
      'the owner undo runs through POST /v1/runners/max-slots/reset, MCP box reset_max_slots and scripts/runner-max-slots-reset.ts, never a console button',
    ),
  },
  // #6558: one machine's 1h / 24h / 7d bucket maxima, both doors over `apps/api`
  // `deps/box-stats-history.ts`. The Fleet page's charts land with #6565.
  'box.history': {
    rest: yes,
    mcp: mcp('box.history'),
    ui: gap('the fleet console has no machine charts yet'),
  },
  'box.update': { rest: yes, mcp: mcp('box.update'), ui: yes },
  // The one row here that is not the caller's own fleet: `runner_releases` is
  // platform-global, so both doors gate it on the operator scope `admin:releases`
  // rather than the fleet role scopes every sibling above rides.
  'box.unyank': { rest: yes, mcp: mcp('platform.unyank'), ui: NO_RELEASE_LEDGER_REPAIR_UI },
  // #6735: the person-attributed promote of a soaked canary build to stable. Same
  // operator scope as `box.unyank`; its console face is the staff-only section of the
  // Fleet page's update dialog (`FleetRolloutModal`).
  'box.promote': { rest: yes, mcp: mcp('platform.promote'), ui: yes },
  'box.enroll': { rest: yes, mcp: BOX_TOKEN_LEG, ui: BOX_TOKEN_LEG },
  'box.hardening_report': { rest: yes, mcp: BOX_TOKEN_LEG, ui: BOX_TOKEN_LEG },
  // Same box-token leg as enroll and the hardening report: the caller is the
  // machine, and the answer is a credential that must reach the box and nothing
  // else — which is exactly why it is not an MCP verb or a console button.
  'box.creds_reissue': { rest: yes, mcp: BOX_TOKEN_LEG, ui: BOX_TOKEN_LEG },
  // A box retiring its OWN row (#6422): the same box-token leg, the caller is the machine.
  'box.self_revoke': { rest: yes, mcp: BOX_TOKEN_LEG, ui: BOX_TOKEN_LEG },
  // Operator-initiated credential re-issue (#2547) — a healthy box, rolled on
  // demand. Sibling of the box-token `box.creds_reissue` above: that one rides
  // the box's own NATS connection and is the box's drive; this one is the
  // operator lever for a suspected key compromise or a path-check. Both share
  // the `creds/reissue` substring in their published paths so the OpenAPI
  // document exposes both. Not an MCP verb until an operator-side agent needs
  // it, and not a UI button until the command center has one to render — the
  // capability lives on REST today.
  'box.reissue_creds': {
    rest: yes,
    // Same `box` verb the rest of the lifecycle plane rides — the operator
    // lever was added on both doors at once (#2547), so an MCP caller has the
    // same answer a REST caller gets.
    mcp: mcp('box.reissue_creds'),
    ui: gap('the console has no operator-issued reissue button yet'),
  },
  'box.join_script': {
    rest: yes,
    mcp: na('a shell script piped into a terminal, not a JSON answer'),
    ui: na('the SPA shows the one-liner to paste; the script itself is fetched by curl'),
  },

  // ── agent templates + prompt vars ─────────────────────────────────────────
  // `/org/:org/account/templates` + `/account/prompt-vars` (`apps/dashboard`).
  // Both `get` rows follow the `webhook.get` precedent: the screen renders from
  // the list, which is the SAME projection (`maskTemplate` / `maskPromptVar`) the
  // point read serves, so there is no field a detail fetch would add.
  'template.list': { rest: yes, mcp: mcp('template.list'), ui: yes },
  'template.get': {
    rest: yes,
    mcp: mcp('template.get'),
    ui: na('the templates screen renders from the list, which carries every field the get does'),
  },
  'template.set': { rest: yes, mcp: mcp('template.set'), ui: yes },
  'template.delete': { rest: yes, mcp: mcp('template.delete'), ui: yes },
  'config.list': { rest: yes, mcp: mcp('config.list'), ui: yes },
  'config.get': {
    rest: yes,
    mcp: mcp('config.get'),
    ui: na(
      'the prompt-vars screen renders from the list, which carries the full value the get does',
    ),
  },
  'config.set': { rest: yes, mcp: mcp('config.set'), ui: yes },
  'config.delete': { rest: yes, mcp: mcp('config.delete'), ui: yes },

  // ── artifacts ─────────────────────────────────────────────────────────────
  'artifact.list': { rest: yes, mcp: mcp('artifact.list'), ui: yes },
  'artifact.get': { rest: yes, mcp: mcp('artifact.get'), ui: yes },
  'artifact.put': { rest: yes, mcp: mcp('artifact.put'), ui: NO_ARTIFACT_UPLOAD_UI },
  'artifact.share': { rest: yes, mcp: mcp('artifact.share'), ui: yes },
  'artifact.delete': { rest: yes, mcp: mcp('artifact.delete'), ui: yes },

  // ── CI ────────────────────────────────────────────────────────────────────
  'ci.runs': { rest: yes, mcp: mcp('ci.runs'), ui: yes },
  'ci.log_url': { rest: yes, mcp: mcp('ci.log_url'), ui: yes },
};
